/* Velora 21+ browser-side gate. No DOB persistence, telemetry or network calls.
   Tab-scoped state expires after 12 hours; client controls are not security boundaries. */
(() => {
  'use strict';
  const gate = document.getElementById('vbl-age-gate');
  if (!gate || gate.dataset.initialized === 'true') return;
  gate.dataset.initialized = 'true';
  const key = 'vbl.ageGate.v1';
  const lifetime = 12 * 60 * 60 * 1000;
  const root = document.documentElement;
  const entry = gate.querySelector('[data-vbl-age-entry]');
  const denied = gate.querySelector('[data-vbl-age-denied]');
  const message = gate.querySelector('[data-vbl-age-denial-message]');
  const error = gate.querySelector('[data-vbl-age-error]');
  const month = gate.querySelector('[data-vbl-age-month]');
  const day = gate.querySelector('[data-vbl-age-day]');
  const year = gate.querySelector('[data-vbl-age-year]');
  let timer;
  let memoryState = null;
  const clearDob = () => { month.value = ''; day.value = ''; year.value = ''; };
  const readState = () => {
    let value = memoryState;
    try { value = JSON.parse(sessionStorage.getItem(key)) || value; } catch (_) { /* no storage: page memory only */ }
    const now = Date.now();
    if (!value || value.version !== 1 || !['approved', 'denied'].includes(value.status) ||
        !Number.isFinite(value.issuedAt) || !Number.isFinite(value.expiresAt) ||
        value.issuedAt > now || value.expiresAt <= now || value.expiresAt - value.issuedAt !== lifetime) {
      memoryState = null;
      try { sessionStorage.removeItem(key); } catch (_) { /* remain unverified */ }
      return null;
    }
    return value;
  };
  const writeState = status => {
    const issuedAt = Date.now();
    memoryState = { version: 1, status, issuedAt, expiresAt: issuedAt + lifetime };
    try { sessionStorage.setItem(key, JSON.stringify(memoryState)); } catch (_) { /* session persistence unavailable */ }
    return memoryState;
  };
  const show = () => {
    root.classList.add('vbl-age-gate-open');
    if (!gate.matches(':modal')) {
      if (gate.open) gate.close();
      gate.showModal();
    }
  };
  const hide = () => { if (gate.open) gate.close(); root.classList.remove('vbl-age-gate-open'); };
  const renderDenied = text => {
    clearTimeout(timer);
    clearDob();
    const transition = gate.dataset.state !== 'denied';
    if (transition) {
      gate.dataset.state = 'denied';
      gate.querySelector('#vbl-age-title').textContent = 'Access unavailable';
      entry.hidden = true;
      denied.hidden = false;
      message.textContent = text;
    }
    show();
    if (transition || !gate.contains(document.activeElement)) message.focus();
  };
  const renderPending = () => {
    clearTimeout(timer);
    if (gate.dataset.state !== 'pending') {
      gate.dataset.state = 'pending';
      gate.querySelector('#vbl-age-title').textContent = 'Confirm Your Age';
      entry.hidden = false;
      denied.hidden = true;
      error.textContent = '';
    }
    show();
  };
  const reconcile = () => {
    const state = readState();
    if (!state) { renderPending(); return; }
    if (state.status === 'denied') { renderDenied('Age verification was declined or did not meet the 21-year minimum.'); return; }
    gate.dataset.state = 'approved';
    clearDob();
    hide();
    clearTimeout(timer);
    timer = setTimeout(reconcile, Math.min(state.expiresAt - Date.now() + 1, lifetime + 1));
  };
  const deny = text => { writeState('denied'); renderDenied(text); };
  const verify = () => {
    if (gate.dataset.state !== 'pending') return;
    const ys = year.value.trim();
    const ms = month.value;
    const ds = day.value.trim();
    const y = Number(ys), m = Number(ms), d = Number(ds);
    const today = new Date();
    const birth = new Date(y, m - 1, d);
    if (!/^\d{4}$/.test(ys) || !/^\d{1,2}$/.test(ds) || !ms || y < 1900 ||
        birth.getFullYear() !== y || birth.getMonth() !== m - 1 || birth.getDate() !== d ||
        birth > today) {
      error.textContent = 'Enter a valid date of birth using month, day and four-digit year.';
      return;
    }
    let age = today.getFullYear() - y;
    if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age -= 1;
    if (age < 21) { deny('You must be 21 or older to enter this storefront.'); return; }
    writeState('approved');
    reconcile();
    const main = document.getElementById('MainContent');
    if (main) main.focus({ preventScroll: true });
  };
  gate.addEventListener('cancel', event => event.preventDefault());
  gate.addEventListener('keydown', event => {
    if (event.key === 'Tab') {
      const controls = [...gate.querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])')].filter(element => element.getClientRects().length);
      if (!controls.length) { event.preventDefault(); message.focus(); return; }
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    if (event.key === 'Enter' && event.target.matches('input,select')) { event.preventDefault(); verify(); }
  });
  gate.querySelector('[data-vbl-age-verify]').addEventListener('click', verify);
  gate.querySelector('[data-vbl-age-decline]').addEventListener('click', () => deny('You declined age verification. Storefront access is unavailable.'));
  window.addEventListener('pageshow', reconcile);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) reconcile(); });
  reconcile();
  gate.dispatchEvent(new Event('vbl-age-controller-ready'));
})();
